import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Users, TrendingUp, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { StatsCard } from '../components/dashboard/StatsCard';
import { RecentActivity } from '../components/dashboard/RecentActivity';
import { ClientMilestones } from '../components/dashboard/ClientMilestones';
import { QuickActions } from '../components/dashboard/QuickActions';
import { WelcomeBanner } from '../components/dashboard/WelcomeBanner';
import { DashboardSkeleton } from '../components/dashboard/DashboardSkeleton';

export function HomePage() {
  const { user } = useAuth();
  const { stats, loading, error, refetch } = useDashboardStats();
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    setFadeIn(true);
  }, []);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Dashboard</h3>
        <p className="text-red-700 mb-4">{error}</p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div
      className={`space-y-6 transition-opacity duration-500 ${
        fadeIn ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <WelcomeBanner />

      {stats?.clientMilestones && stats.clientMilestones.length > 0 && (
        <ClientMilestones milestones={stats.clientMilestones} />
      )}

      {stats?.clientsNeedingFollowup && stats.clientsNeedingFollowup.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-amber-900 mb-3">
            Clients Needing Follow-up
          </h3>
          <p className="text-sm text-amber-700 mb-4">
            These clients haven't had a session in over 14 days:
          </p>
          <div className="space-y-2">
            {stats.clientsNeedingFollowup.slice(0, 5).map((client: any) => (
              <Link
                key={client.id}
                to={`/app/clients/${client.id}`}
                className="block p-3 bg-white rounded-lg hover:bg-amber-100 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900">{client.first_name}</span>
                  <span className="text-sm text-gray-600">
                    Last session: {new Date(client.last_session).toLocaleDateString()}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <QuickActions />

      <div className="bg-gradient-to-b from-gray-50/50 to-transparent rounded-xl p-6 -mx-2">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">At A Glance</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatsCard
            title="Today"
            value={stats?.sessionsToday || 0}
            subtitle="sessions"
            icon={Calendar}
            compact
          />
          <StatsCard
            title="This Month"
            value={stats?.sessionsThisMonth || 0}
            subtitle="sessions"
            icon={Calendar}
            compact
          />
          <StatsCard
            title="All Time"
            value={stats?.totalSessions || 0}
            subtitle="total sessions"
            icon={Calendar}
            compact
          />
          <StatsCard
            title="Notes This Week"
            value={stats?.notesThisWeek || 0}
            change={stats?.notesChange}
            icon={FileText}
            trend={stats?.notesTrend}
            compact
          />
          <StatsCard
            title="Clients Seen"
            value={stats?.clientsSeenThisWeek || 0}
            change={stats?.clientsChange}
            icon={Users}
            trend={stats?.clientsTrend}
            compact
          />
          <StatsCard
            title="Active Clients"
            value={stats?.activeClients || 0}
            subtitle={`${stats?.inactiveClients || 0} inactive`}
            icon={TrendingUp}
            compact
          />
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <RecentActivity activities={stats?.recentActivities || []} />
      </div>
    </div>
  );
}
